import { phoneCountryCodeOptions } from "../../../shared/config/phoneOptions";

export const getAge = (dob) => {
  if (!dob) {
    return null;
  }

  const birthDate = new Date(dob);

  if (Number.isNaN(birthDate.getTime())) {
    return null;
  }

  const today = new Date();

  let age =
    today.getFullYear() -
    birthDate.getFullYear();

  const monthDifference =
    today.getMonth() -
    birthDate.getMonth();

  const birthdayNotReached =
    monthDifference < 0 ||
    (
      monthDifference === 0 &&
      today.getDate() <
        birthDate.getDate()
    );

  if (birthdayNotReached) {
    age -= 1;
  }

  return age;
};

const hasStrongPassword = (value) =>
  /[A-Z]/.test(value) &&
  /[a-z]/.test(value) &&
  /\d/.test(value) &&
  /[^A-Za-z0-9]/.test(value);

const isValidLocalPhoneNumber = (value) =>
  /^\d{10}$/.test(
    String(value || "")
      .replace(/\D/g, "")
  );

const getLocalNumberFromCombinedPhone = (value) => {
  const normalized = String(value || "").trim();

  if (!normalized) {
    return "";
  }

  if (!normalized.startsWith("+")) {
    return normalized.replace(/\D/g, "");
  }

  const matchingCode = phoneCountryCodeOptions
    .map((option) => option.code)
    .filter(Boolean)
    .sort((left, right) => right.length - left.length)
    .find((code) => normalized.startsWith(code));

  if (!matchingCode) {
    return "";
  }

  return normalized
    .slice(matchingCode.length)
    .replace(/\D/g, "");
};

const validateOptionalCombinedPhone = (
  errors,
  formData,
  field,
  label
) => {
  const value = formData[field];

  if (!value) {
    return;
  }

  const localNumber =
    getLocalNumberFromCombinedPhone(value);

  if (!isValidLocalPhoneNumber(localNumber)) {
    errors[field] =
      `${label} must contain a valid 10-digit phone number.`;
  }
};

export const validateRegistration = (
  formData
) => {
  const errors = {};

  const requiredFields = {
    profileCreatedFor:
      "This Profile is For",
    name: "Full Name",
    gender: "Gender",
    motherTongue: "Mother Tongue",
    currentLocationCountry:
      "Residing Country",
    phoneNumber: "Phone Number",
    email: "Email Address",
    dob: "Date of Birth",
    password: "Password",
    confirmPassword:
      "Confirm Password",
    marriedStatus:
      "Marital Status",
    heightFeet: "Height Feet",
    heightInches:
      "Height Inches",
    profileCategory:
      "Bride/Groom Category",
    gotra: "Gotra",
    rashi: "Rashi",
    nakshatra: "Nakshatra",
    charanaPada:
      "Charana Pada",
    subCaste: "Sub Caste",
    guruMatha: "Guru Matha",
    education: "Education",
    profession: "Profession",
    workingStatus:
      "Working Status",
    annualIncome:
      "Annual Income",
    aboutBrideGroom:
      "About Yourself",
    fatherName:
      "Father's Name",
    fatherProfession:
      "Father's Profession",
    motherName:
      "Mother's Name",
    motherProfession:
      "Mother's Profession",
    noOfBrothers:
      "No. of Brothers",
    noOfSisters:
      "No. of Sisters",
    familyStatus:
      "Family Status",
    familyType:
      "Family Type",
    familyValues:
      "Family Values",
    reference1Name:
      "Reference 1 Name",
    reference1Phone:
      "Reference 1 Phone",
  };

  Object.entries(
    requiredFields
  ).forEach(([field, label]) => {
    const value = formData[field];

    if (
      value === null ||
      value === undefined ||
      String(value).trim() === ""
    ) {
      errors[field] =
        `${label} is required.`;
    }
  });

  if (formData.email) {
    const email =
      formData.email.trim();

    if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
        email
      )
    ) {
      errors.email =
        "Valid email is required.";
    }
  }

  if (!formData.profileId) {
    errors.profileId =
      "Unable to generate Profile ID. Please verify the name and mobile number.";
  }

  if (formData.phoneNumber) {
    if (
      !isValidLocalPhoneNumber(
        formData.phoneNumber
      )
    ) {
      errors.phoneNumber =
        "Valid 10-digit phone number is required.";
    }
  }

  validateOptionalCombinedPhone(
    errors,
    formData,
    "alternatePhone",
    "Alternate Phone"
  );

  validateOptionalCombinedPhone(
    errors,
    formData,
    "guardianPhone",
    "Guardian Phone"
  );

  validateOptionalCombinedPhone(
    errors,
    formData,
    "reference1Phone",
    "Reference 1 Phone"
  );

  validateOptionalCombinedPhone(
    errors,
    formData,
    "reference2Phone",
    "Reference 2 Phone"
  );

  if (formData.dob) {
    const age = getAge(
      formData.dob
    );

    if (
      age === null ||
      age < 18
    ) {
      errors.dob =
        "Minimum age must be 18 years.";
    }
  }

  if (formData.password) {
    if (
      formData.password.length < 8 ||
      !hasStrongPassword(
        formData.password
      )
    ) {
      errors.password =
        "Password must be at least 8 characters and include uppercase, lowercase, number and special character.";
    }
  }

  if (
    formData.password &&
    formData.confirmPassword &&
    formData.password !==
      formData.confirmPassword
  ) {
    errors.confirmPassword =
      "Password and Confirm Password must match.";
  }

  const aboutLength =
    String(
      formData.aboutBrideGroom || ""
    ).trim().length;

  if (
    formData.aboutBrideGroom &&
    aboutLength < 50
  ) {
    errors.aboutBrideGroom =
      "About Yourself must contain at least 50 characters.";
  }

  if (aboutLength > 1000) {
    errors.aboutBrideGroom =
      "About Yourself cannot exceed 1000 characters.";
  }

  if (
    !formData.declarationAccepted
  ) {
    errors.declarationAccepted =
      "Please accept the declaration before creating the profile.";
  }

  return errors;
};

export const getFirstValidationMessage = (
  errors
) => {
  const firstKey =
    Object.keys(errors)[0];

  return firstKey
    ? errors[firstKey]
    : "";
};
